import { forwardRef, type HTMLAttributes } from "react";
import { Badge, type BadgeSize } from "./Badge";
import { DISCIPLINES, type DisciplineSlug } from "@jumpbean/shared";

export interface BadgeInfo {
  disciplineSlug: DisciplineSlug;
  levelNumber: number;
  isEarned: boolean;
  earnedAt?: string;
}

export interface BadgeGridProps extends HTMLAttributes<HTMLDivElement> {
  /** Array of badge info to display */
  badges: BadgeInfo[];
  /** Size of individual badges */
  size?: BadgeSize;
  /** Whether to show badge labels */
  showLabels?: boolean;
  /** Filter to show only earned badges */
  showOnlyEarned?: boolean;
  /** Group badges by discipline */
  groupByDiscipline?: boolean;
  /** Callback when a badge is clicked */
  onBadgeClick?: (badge: BadgeInfo) => void;
  /** Empty state message when no badges */
  emptyMessage?: string;
}

/**
 * BadgeGrid displays a collection of badges, optionally grouped by discipline.
 * Perfect for showcasing earned achievements or showing progress toward badges.
 *
 * @example
 * // Simple grid of all badges
 * <BadgeGrid badges={childBadges} size="md" showLabels />
 *
 * @example
 * // Grouped by discipline with click handler
 * <BadgeGrid
 *   badges={allBadges}
 *   groupByDiscipline
 *   onBadgeClick={(badge) => console.log('Clicked:', badge)}
 * />
 */
export const BadgeGrid = forwardRef<HTMLDivElement, BadgeGridProps>(
  (
    {
      badges,
      size = "md",
      showLabels = false,
      showOnlyEarned = false,
      groupByDiscipline = false,
      onBadgeClick,
      emptyMessage = "No badges earned yet. Keep playing to collect them all!",
      className = "",
      ...props
    },
    ref
  ) => {
    // Filter badges if needed
    const filteredBadges = showOnlyEarned
      ? badges.filter((b) => b.isEarned)
      : badges;

    // Show empty state if no badges
    if (filteredBadges.length === 0) {
      return (
        <div
          ref={ref}
          className={`
            flex flex-col items-center justify-center
            p-8 text-center
            ${className}
          `}
          {...props}
        >
          <div className="w-20 h-20 rounded-full bg-neutral-100 flex items-center justify-center mb-4">
            <EmptyBadgeIcon className="w-10 h-10 text-neutral-400" />
          </div>
          <p className="font-body text-body-lg text-neutral-500 max-w-xs">
            {emptyMessage}
          </p>
        </div>
      );
    }

    // Group badges by discipline if requested
    if (groupByDiscipline) {
      const groupedBadges = groupBadgesByDiscipline(filteredBadges);

      return (
        <div
          ref={ref}
          className={`space-y-8 ${className}`}
          {...props}
        >
          {Object.entries(groupedBadges).map(([discipline, disciplineBadges]) => (
            <DisciplineGroup
              key={discipline}
              discipline={discipline as DisciplineSlug}
              badges={disciplineBadges}
              size={size}
              showLabels={showLabels}
              onBadgeClick={onBadgeClick}
            />
          ))}
        </div>
      );
    }

    // Simple grid layout
    return (
      <div
        ref={ref}
        className={`
          grid gap-4
          grid-cols-3 sm:grid-cols-4 md:grid-cols-5
          ${className}
        `}
        {...props}
      >
        {filteredBadges.map((badge) => (
          <BadgeItem
            key={`${badge.disciplineSlug}-${badge.levelNumber}`}
            badge={badge}
            size={size}
            showLabel={showLabels}
            onClick={onBadgeClick ? () => onBadgeClick(badge) : undefined}
          />
        ))}
      </div>
    );
  }
);

BadgeGrid.displayName = "BadgeGrid";

// Helper to group badges by discipline
function groupBadgesByDiscipline(badges: BadgeInfo[]): Record<DisciplineSlug, BadgeInfo[]> {
  const groups: Partial<Record<DisciplineSlug, BadgeInfo[]>> = {};

  for (const badge of badges) {
    if (!groups[badge.disciplineSlug]) {
      groups[badge.disciplineSlug] = [];
    }
    groups[badge.disciplineSlug]!.push(badge);
  }

  // Sort badges within each discipline by level
  for (const discipline of Object.keys(groups) as DisciplineSlug[]) {
    groups[discipline]!.sort((a, b) => a.levelNumber - b.levelNumber);
  }

  return groups as Record<DisciplineSlug, BadgeInfo[]>;
}

// Discipline group component
interface DisciplineGroupProps {
  discipline: DisciplineSlug;
  badges: BadgeInfo[];
  size: BadgeSize;
  showLabels: boolean;
  onBadgeClick?: (badge: BadgeInfo) => void;
}

function DisciplineGroup({
  discipline,
  badges,
  size,
  showLabels,
  onBadgeClick,
}: DisciplineGroupProps) {
  const disciplineInfo = DISCIPLINES[discipline];
  const earnedCount = badges.filter((b) => b.isEarned).length;

  // Discipline color classes
  const colorClasses: Record<DisciplineSlug, string> = {
    math: "text-luna-dark border-luna",
    spelling: "text-petal-dark border-petal",
    patterns: "text-prism-purple border-prism-purple",
    typing: "text-whiskers-dark border-whiskers",
    programming: "text-pixel-dark border-pixel",
  };

  const bgClasses: Record<DisciplineSlug, string> = {
    math: "bg-luna-light/30",
    spelling: "bg-petal-light/30",
    patterns: "bg-gradient-to-r from-prism-red/10 via-prism-yellow/10 to-prism-blue/10",
    typing: "bg-whiskers-light/30",
    programming: "bg-pixel-light/30",
  };

  return (
    <div className={`rounded-2xl p-5 ${bgClasses[discipline]}`}>
      {/* Discipline header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className={`
              w-10 h-10 rounded-xl flex items-center justify-center
              border-2 ${colorClasses[discipline]}
              bg-cloud
            `}
          >
            <DisciplineIcon discipline={discipline} className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-display font-bold text-body-lg text-neutral-800">
              {disciplineInfo.name}
            </h3>
            <p className="font-body text-body-sm text-neutral-500">
              {disciplineInfo.characterName}
            </p>
          </div>
        </div>

        {/* Progress indicator */}
        <div className="flex items-center gap-2">
          <span className="font-display font-bold text-body-lg text-neutral-800">
            {earnedCount}
          </span>
          <span className="font-body text-body-sm text-neutral-500">
            / {badges.length}
          </span>
        </div>
      </div>

      {/* Badges row */}
      <div className="flex flex-wrap gap-3">
        {badges.map((badge) => (
          <BadgeItem
            key={`${badge.disciplineSlug}-${badge.levelNumber}`}
            badge={badge}
            size={size}
            showLabel={showLabels}
            onClick={onBadgeClick ? () => onBadgeClick(badge) : undefined}
          />
        ))}
      </div>
    </div>
  );
}

// Individual badge item with optional click handler
interface BadgeItemProps {
  badge: BadgeInfo;
  size: BadgeSize;
  showLabel: boolean;
  onClick?: () => void;
}

function BadgeItem({ badge, size, showLabel, onClick }: BadgeItemProps) {
  const Wrapper = onClick ? "button" : "div";

  return (
    <Wrapper
      onClick={onClick}
      className={`
        ${onClick ? "cursor-pointer focus:outline-none focus-visible:ring-4 focus-visible:ring-fairy-400 rounded-full" : ""}
        transition-transform duration-200
        ${onClick && badge.isEarned ? "hover:scale-110 active:scale-95" : ""}
      `}
      type={onClick ? "button" : undefined}
      aria-label={
        onClick
          ? `View ${badge.disciplineSlug} level ${badge.levelNumber} badge`
          : undefined
      }
    >
      <Badge
        discipline={badge.disciplineSlug}
        level={badge.levelNumber}
        isEarned={badge.isEarned}
        size={size}
        showLabel={showLabel}
      />
    </Wrapper>
  );
}

// Discipline icons
function DisciplineIcon({
  discipline,
  className,
}: {
  discipline: DisciplineSlug;
  className?: string;
}) {
  const icons: Record<DisciplineSlug, React.ReactNode> = {
    math: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
      </svg>
    ),
    spelling: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <circle cx="12" cy="12" r="3" />
        <ellipse cx="12" cy="5" rx="3" ry="4" />
        <ellipse cx="12" cy="19" rx="3" ry="4" />
        <ellipse cx="5" cy="12" rx="4" ry="3" />
        <ellipse cx="19" cy="12" rx="4" ry="3" />
      </svg>
    ),
    patterns: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M2 17a10 10 0 0 1 20 0" strokeLinecap="round" />
        <path d="M5 17a7 7 0 0 1 14 0" strokeLinecap="round" />
        <path d="M8 17a4 4 0 0 1 8 0" strokeLinecap="round" />
      </svg>
    ),
    typing: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M15 4l-11 11 4 4 11-11-4-4z" />
        <path d="M20.5 3.5L18 6l-1.5-1.5 2.5-2.5c.4-.4 1-.4 1.4 0l.1.1c.4.4.4 1 0 1.4z" />
        <circle cx="6" cy="4" r="1" />
        <circle cx="4" cy="8" r="1" />
        <circle cx="9" cy="3" r="1" />
      </svg>
    ),
    programming: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
  };

  return <>{icons[discipline]}</>;
}

// Empty badge placeholder icon
function EmptyBadgeIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <circle cx="12" cy="12" r="9" strokeDasharray="4 2" />
      <path d="M12 8v8M8 12h8" strokeLinecap="round" />
    </svg>
  );
}

export default BadgeGrid;
